<script setup lang="ts">
  import Chip from '@leaflink/stash/Chip.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import { RouteLocationRaw, RouterLink, useRoute, useRouter } from 'vue-router';

  import { SPLIT_NAMES } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';

  import { ResolvedAppNavigationConfigItem } from './config';

  const router = useRouter();
  const route = useRoute();

  export interface AppNavigationItemProps {
    item: ResolvedAppNavigationConfigItem;
    forceReload?: boolean;
  }
  const props = withDefaults(defineProps<AppNavigationItemProps>(), {
    forceReload: false,
  });

  const emit = defineEmits<{
    (e: 'navigate'): void;
  }>();

  const isOpen = ref(false);
  const { isDelinquent: getIsDelinquent, isLicenseLocked: getIsLicenseLocked } = storeToRefs(useCompanyStore());

  const { getIsSplitEnabled: isSplitEnabled } = storeToRefs(useUserStore());

  const isDelinquent = computed(() => getIsDelinquent.value(isSplitEnabled.value(SPLIT_NAMES.DELINQUENT_LOCKDOWN)));
  const isSSRIframeEnabled = computed(() => isSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME));
  const isLicenseLocked = computed(() =>
    getIsLicenseLocked.value(isSplitEnabled.value(SPLIT_NAMES.PAUSE_INACTIVE_LICENSE_COMPANIES)),
  );
  const goTo = computed<RouteLocationRaw>(() => {
    const resolved = router.resolve(props.item.href || (props.item.route as RouteLocationRaw));

    return { path: resolved.path, force: props.forceReload };
  });

  /**
   * Assigns the active class if it's not handled by the config
   *
   * This attempts to match the URL ignoring the path params.
   */
  const isActive = computed(() => {
    if (props.item.active !== undefined) {
      return props.item.active;
    }

    const resolved = router.resolve(props.item.route as RouteLocationRaw);
    const itemPath = resolved.href;
    const currentPath = route.path;

    return itemPath === currentPath || currentPath.startsWith(itemPath);
  });
</script>

<template>
  <li
    :data-test="isActive ? 'li|active-nav-item' : null"
    :class="['relative block', $style.item, { [$style['is-active']]: isActive, [$style['is-open']]: isOpen }]"
  >
    <span v-if="props.item.active" :class="['absolute', $style['active-marker']]"></span>
    <span v-if="isDelinquent || isLicenseLocked" :class="[$style.anchor, $style.disabled]">
      <Icon class="mr-1" :name="props.item.icon" data-test="icon|nav-link" />
      {{ i18n.t(props.item.label) }}
    </span>
    <template v-else>
      <button v-if="props.item.children" :class="['text-left', $style.toggle, $style.anchor]" @click="isOpen = !isOpen">
        <Icon class="mr-1" :name="props.item.icon" data-test="icon|nav-link" />

        {{ i18n.t(props.item.label) }}

        <Icon name="chevron-left" size="small" :class="['ml-auto', { [$style.down]: isOpen }]" />
      </button>
      <!-- external links -->
      <!-- TODO add financial.leaflink.com to routes-->

      <a
        v-else-if="!isSSRIframeEnabled && props.item.href"
        :class="$style.anchor"
        :href="props.item.href"
        @click="emit('navigate')"
      >
        <Icon class="mr-1" :name="props.item.icon" data-test="icon|nav-link" />
        {{ i18n.t(props.item.label) }}
      </a>

      <RouterLink v-else :class="$style.anchor" :to="goTo" @click="emit('navigate')">
        <Icon class="mr-1" :name="props.item.icon" data-test="icon|nav-link" />
        {{ i18n.t(props.item.label) }}
        <Chip
          v-if="props.item.badge?.count"
          class="ml-auto"
          radius="pill"
          shade="main"
          :color-scheme="props.item.badge.color || 'blue'"
        >
          {{ props.item.badge.count }}
        </Chip>
        <Icon
          v-if="props.item.warning"
          class="text-orange color-orange mr-0 ml-auto"
          name="circle-warning"
          data-test="icon|nav-link-warning"
        />
      </RouterLink>

      <ul v-if="props.item.children && isOpen" :class="['pl-0', $style.subnav]">
        <AppNavigationItem
          v-for="(child, index) in props.item.children"
          :item="child"
          :key="index"
          @navigate="emit('navigate')"
        />
      </ul>
    </template>
  </li>
</template>

<style lang="scss" module>
  .anchor {
    align-items: center;
    color: var(--color-ice);
    cursor: pointer;
    display: flex;
    padding: 8px 18px; // Adjust these for spacing vars?
    text-decoration: none;

    &:hover {
      background: var(--color-purple-700);
      color: var(--color-ice);
      text-decoration: none;
    }

    &.disabled {
      color: var(--color-purple-300);
      pointer-events: none;
    }
  }

  .subnav {
    .anchor {
      padding-left: space(4);
    }
  }

  .is-active .anchor,
  .is-active .anchor:hover {
    background: var(--color-purple-700);
    color: var(--color-seafoam);
  }

  .active-marker {
    background: var(--color-seafoam);
    height: 100%;
    width: 6px;
    z-index: 100;
  }

  .down {
    transform: rotate(-90deg);
  }

  .item {
    font-weight: 500;
    line-height: 1.5;
    outline: none;
  }

  .is-open {
    button {
      background: #263256;
    }
  }

  .toggle {
    outline: none;
    width: 100%;
  }
</style>
